import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CALCULATED_COLUMN_OPS, normalizeCalculatedColumns } from '../utils/calculatedColumns';

/**
 * Alta y baja de columnas calculadas. `columns` son las claves de la fila;
 * `labels` (opcional) es el nombre visible de cada clave.
 */
export default function CalculatedColumnsEditor({
  columns = [],
  labels = {},
  value = [],
  onChange,
  dense = false,
  collapsible = true,
}) {
  const [label, setLabel] = useState('');
  const [left, setLeft] = useState('');
  const [op, setOp] = useState('add');
  const [right, setRight] = useState('');
  const [round, setRound] = useState(false);
  const [decimalPlaces, setDecimalPlaces] = useState('');
  const [percent, setPercent] = useState(false);
  const numericOp = op !== 'concat';
  const defs = normalizeCalculatedColumns(value);
  const [collapsed, setCollapsed] = useState(() => defs.length === 0);
  const prevDefCountRef = useRef(defs.length);

  useEffect(() => {
    const prev = prevDefCountRef.current;
    prevDefCountRef.current = defs.length;
    if (defs.length === 0) setCollapsed(true);
    else if (prev === 0 && defs.length > 0) setCollapsed(false);
  }, [defs.length]);
  const taken = new Set([
    ...columns,
    ...defs.map((def) => def.label),
  ]);
  const name = label.trim();
  const nameClash = name !== '' && taken.has(name);
  const canAdd = Boolean(name && left && right && !nameClash && columns.length >= 2);

  const add = () => {
    if (!canAdd) return;
    const id = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    onChange?.([
      ...defs,
      {
        id,
        key: `calc_${id}`,
        label: name,
        left,
        op,
        right,
        ...(numericOp && round ? { round: true } : {}),
        ...(numericOp && decimalPlaces !== '' ? { decimalPlaces: Number(decimalPlaces) } : {}),
        ...(numericOp && percent ? { percent: true } : {}),
      },
    ]);
    setLabel('');
    setRound(false);
    setDecimalPlaces('');
    setPercent(false);
  };

  const patchDef = (id, patch) => {
    onChange?.(defs.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const onDecimalChange = (raw, apply) => {
    const v = String(raw).trim();
    if (v === '') {
      apply(undefined);
      return;
    }
    const n = Math.min(10, Math.max(0, parseInt(v, 10) || 0));
    apply(n);
  };

  const columnLabel = (key) => labels[key] || key;
  const fieldSize = dense ? 'small' : 'small';

  const titleVariant = dense ? 'caption' : 'subtitle2';

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        minHeight: 28,
        ...(collapsible ? { cursor: 'pointer', userSelect: 'none' } : {}),
      }}
      onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
      onKeyDown={
        collapsible
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setCollapsed((c) => !c);
              }
            }
          : undefined
      }
      role={collapsible ? 'button' : undefined}
      tabIndex={collapsible ? 0 : undefined}
      aria-expanded={collapsible ? !collapsed : undefined}
    >
      {collapsible && (
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
          aria-label={collapsed ? 'Expandir columnas calculadas' : 'Contraer columnas calculadas'}
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed((c) => !c);
          }}
        >
          {collapsed ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      )}
      <Typography variant={titleVariant} fontWeight={dense ? 700 : undefined} sx={{ flex: 1 }}>
        Columnas calculadas
        {collapsible && collapsed && defs.length > 0 ? (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
            ({defs.length})
          </Typography>
        ) : null}
      </Typography>
    </Box>
  );

  const body = (
    <>
      <Typography variant="caption" color="text.secondary">
        Para cada fila, con dos columnas de esa misma fila. No cambia la consulta.
      </Typography>
      {columns.length < 2 && (
        <Typography variant="caption" color="text.secondary">
          Hace falta el resultado de al menos dos columnas.
        </Typography>
      )}
      {defs.map((def) => {
        const opLabel = CALCULATED_COLUMN_OPS.find((item) => item.value === def.op)?.label || def.op;
        return (
          <Box key={def.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap title={def.key}>
                {def.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {def.key} · {columnLabel(def.left)} · {opLabel} · {columnLabel(def.right)}
              </Typography>
              {def.op !== 'concat' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={Boolean(def.round)}
                        onChange={(e) => patchDef(def.id, { round: e.target.checked })}
                      />
                    )}
                    label={<Typography variant="caption">Redondear</Typography>}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Decimales"
                    placeholder="Auto"
                    value={def.decimalPlaces ?? ''}
                    onChange={(e) => onDecimalChange(e.target.value, (n) => patchDef(def.id, { decimalPlaces: n }))}
                    inputProps={{ min: 0, max: 10, step: 1 }}
                    sx={{ width: 100 }}
                  />
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={Boolean(def.percent)}
                        onChange={(e) => patchDef(def.id, { percent: e.target.checked })}
                      />
                    )}
                    label={<Typography variant="caption">Porcentaje</Typography>}
                  />
                </Box>
              )}
            </Box>
            <IconButton
              size="small"
              title="Quitar columna calculada"
              onClick={() => onChange?.(defs.filter((item) => item.id !== def.id))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
      <TextField
        size={fieldSize}
        label="Nombre"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        error={nameClash}
        helperText={nameClash ? 'Ese nombre ya es una columna' : ' '}
        disabled={columns.length < 2}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <FormControl size={fieldSize} sx={{ minWidth: 140, flex: 1 }} disabled={columns.length < 2}>
          <InputLabel>Columna A</InputLabel>
          <Select label="Columna A" value={left} onChange={(e) => setLeft(e.target.value)}>
            {columns.map((key) => (
              <MenuItem key={`a-${key}`} value={key}>{columnLabel(key)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size={fieldSize} sx={{ minWidth: 140 }}>
          <InputLabel>Operación</InputLabel>
          <Select label="Operación" value={op} onChange={(e) => setOp(e.target.value)}>
            {CALCULATED_COLUMN_OPS.map((item) => (
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size={fieldSize} sx={{ minWidth: 140, flex: 1 }} disabled={columns.length < 2}>
          <InputLabel>Columna B</InputLabel>
          <Select label="Columna B" value={right} onChange={(e) => setRight(e.target.value)}>
            {columns.map((key) => (
              <MenuItem key={`b-${key}`} value={key}>{columnLabel(key)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {numericOp && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={(
              <Checkbox
                size="small"
                checked={round}
                onChange={(e) => setRound(e.target.checked)}
                disabled={columns.length < 2}
              />
            )}
            label={<Typography variant="caption">Redondear</Typography>}
          />
          <TextField
            size={fieldSize}
            type="number"
            label="Decimales"
            placeholder="Auto"
            value={decimalPlaces}
            disabled={columns.length < 2}
            onChange={(e) => onDecimalChange(e.target.value, (n) => setDecimalPlaces(n == null ? '' : String(n)))}
            inputProps={{ min: 0, max: 10, step: 1 }}
            sx={{ width: 110 }}
          />
          <FormControlLabel
            sx={{ mr: 0 }}
            control={(
              <Checkbox
                size="small"
                checked={percent}
                onChange={(e) => setPercent(e.target.checked)}
                disabled={columns.length < 2}
              />
            )}
            label={<Typography variant="caption">Porcentaje</Typography>}
          />
          <Typography variant="caption" color="text.secondary">
            Con decimales, el número se muestra redondeado a esa cantidad. Redondear, si no hay decimales, deja el entero más cercano. Porcentaje muestra el resultado multiplicado por 100, con el signo %.
          </Typography>
        </Box>
      )}
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        disabled={!canAdd}
        onClick={add}
        sx={{ alignSelf: 'flex-start' }}
      >
        Agregar columna
      </Button>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: collapsed && collapsible ? 0 : 1 }}>
      {header}
      {collapsible ? (
        <Collapse in={!collapsed} timeout="auto" unmountOnExit={false}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>{body}</Box>
        </Collapse>
      ) : (
        body
      )}
    </Box>
  );
}
